import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import { bookkeepersAPI } from '../services/api';

const BookkeeperEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bookkeeper, setBookkeeper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchBookkeeper();
  }, [id]);

  const fetchBookkeeper = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await bookkeepersAPI.getById(id);
      if (response.data.success) {
        setBookkeeper(response.data.data);
      } else {
        setError('Failed to load bookkeeper details');
      }
    } catch (err) {
      console.error('Error fetching bookkeeper:', err);
      setError('Failed to load bookkeeper details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setBookkeeper(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field, value) => {
    // Convert comma-separated string to array
    const arrayValue = value.split(',').map(item => item.trim()).filter(item => item);
    handleInputChange(field, arrayValue);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Prepare data for update (only send fields that can be updated)
      const updateData = {
        fullName: bookkeeper.fullName,
        email: bookkeeper.email,
        phone: bookkeeper.phone,
        address: bookkeeper.address,
        city: bookkeeper.city,
        state: bookkeeper.state,
        zipCode: bookkeeper.zipCode,
        companyName: bookkeeper.companyName,
        companyAddress: bookkeeper.companyAddress,
        bio: bookkeeper.bio,
        experience: bookkeeper.experience,
        hourlyRate: bookkeeper.hourlyRate,
        qualifications: bookkeeper.qualifications,
        specialties: bookkeeper.specialties,
        status: bookkeeper.status,
        isActive: bookkeeper.isActive,
        isVerified: bookkeeper.isVerified,
        preferredContact: bookkeeper.preferredContact
      };

      const response = await bookkeepersAPI.update(id, updateData);
      
      if (response.data.success) {
        setSuccess('Bookkeeper updated successfully!');
        setTimeout(() => {
          navigate(`/bookkeepers/${id}`);
        }, 1500);
      } else {
        setError(response.data.message || 'Failed to update bookkeeper');
      }
    } catch (err) {
      console.error('Error updating bookkeeper:', err);
      setError(err.response?.data?.message || 'Failed to update bookkeeper. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading Bookkeeper Details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!bookkeeper) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Bookkeeper not found
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/bookkeepers')}>
          Back to Bookkeepers
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={() => navigate(`/bookkeepers/${id}`)}
          sx={{ mb: 2 }}
        >
          Back to Details
        </Button>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Edit Bookkeeper
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              {bookkeeper.fullName}
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Personal Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                Personal Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name *"
                value={bookkeeper.fullName || ''}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={bookkeeper.email || ''}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={bookkeeper.phone || ''}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Preferred Contact</InputLabel>
                <Select
                  value={bookkeeper.preferredContact || 'email'}
                  label="Preferred Contact"
                  onChange={(e) => handleInputChange('preferredContact', e.target.value)}
                >
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="phone">Phone</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Address Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                Address Information
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={bookkeeper.address || ''}
                onChange={(e) => handleInputChange('address', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={bookkeeper.city || ''}
                onChange={(e) => handleInputChange('city', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State"
                value={bookkeeper.state || ''}
                onChange={(e) => handleInputChange('state', e.target.value)}
                placeholder="e.g., CA"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={bookkeeper.zipCode || ''}
                onChange={(e) => handleInputChange('zipCode', e.target.value)}
              />
            </Grid>

            {/* Professional Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                Professional Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Name"
                value={bookkeeper.companyName || ''}
                onChange={(e) => handleInputChange('companyName', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Address"
                value={bookkeeper.companyAddress || ''}
                onChange={(e) => handleInputChange('companyAddress', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Experience (years)"
                type="number"
                value={bookkeeper.experience || ''}
                onChange={(e) => handleInputChange('experience', parseInt(e.target.value) || 0)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Hourly Rate ($)"
                type="number"
                value={bookkeeper.hourlyRate || ''}
                onChange={(e) => handleInputChange('hourlyRate', parseFloat(e.target.value) || 0)}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Bio"
                value={bookkeeper.bio || ''}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                placeholder="Tell us about your experience and expertise..."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Qualifications"
                value={bookkeeper.qualifications ? bookkeeper.qualifications.join(', ') : ''}
                onChange={(e) => handleArrayChange('qualifications', e.target.value)}
                placeholder="CPA, QuickBooks Certified, etc. (comma separated)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Specialties"
                value={bookkeeper.specialties ? bookkeeper.specialties.join(', ') : ''}
                onChange={(e) => handleArrayChange('specialties', e.target.value)}
                placeholder="Small Business, Tax Preparation, etc. (comma separated)"
              />
            </Grid>

            {/* Status & Settings */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                Status & Settings
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status *</InputLabel>
                <Select
                  value={bookkeeper.status || 'pending'}
                  label="Status *"
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  required
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={bookkeeper.isActive !== false}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    />
                  }
                  label="Active"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={bookkeeper.isVerified || false}
                      onChange={(e) => handleInputChange('isVerified', e.target.checked)}
                    />
                  }
                  label="Verified"
                />
              </Box>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/bookkeepers/${id}`)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                  disabled={saving}
                  size="large"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default BookkeeperEdit;